import axios from 'axios';
import { useMainStore } from '@/stores/main';

const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]

// Dynamically select baseUrl based on hostname
function getBaseUrl() {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:3000';
    } else if (hostname === 'pamv2.hmgbs.com') {
        return 'https://pamv2.backend.hmgbs.com';
    } else {
        return 'https://pam.backend.hmgbs.com';
    }
}
const baseUrl = getBaseUrl()

async function sendAPIRequest(endpoint, method, dataOrParams, storeKey = null) {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");
    store.setIndicateLoading(true);

    await axios({
        method: method,
        url: baseUrl + endpoint,
        data: method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE" ? dataOrParams: {},
        params: method === "GET" ? dataOrParams: {},
        headers: {
            Authorization: 'Bearer ' + accessToken
        }
    }).then(function(response) {
        if (storeKey != null) {
            store[storeKey](response.data);
        }
        store.setIndicateLoading(false);
    }).catch(function(error) {
        console.log(error)
        store.setIndicateError(true);
        throw error; // rethrow error to handle up the stack
    })
}

async function getSellingSidePlatform() {
    await sendAPIRequest("/sellingSidePlatform", "GET", {}, "setSellingSidePlatforms")
}

// patch SSP to update user note
async function patchSellingSidePlatform(sspName, userNote) {
    await sendAPIRequest("/sellingsideplatform", "PATCH", {
        "ssp_name": sspName,
        "ssp_userNote": userNote
    })
}

async function getRevenue() {
    await sendAPIRequest("/revenue", "GET", {}, "setRevenue");
}

async function getCampaignInfo() {
    await sendAPIRequest("/campaignInfo", "GET", {}, "setCampaignInfo");
}

async function deleteRevenue(selectedYear, selectedMonth, connectionType, source) {
    await sendAPIRequest("/revenue", "DELETE", {
        "rev_year": selectedYear,
        "rev_month": selectedMonth,
        "rev_connectionType": connectionType,
        "rev_source": source,
    })
}

async function putRevenue(selectedYear, selectedMonth, connectionType, source, n2, isInvoiceFromPBI, isUSD, originalUsdAmount, usdConversionRate, usdConversionDate) {
    await sendAPIRequest("/revenue","PUT", {
        "rev_year": selectedYear,
        "rev_month": selectedMonth,
        "rev_connectionType": connectionType,
        "rev_source": source,
        "rev_n2": n2,
        "rev_isUSD": isUSD,
        "rev_invoiceFromPBI": isInvoiceFromPBI,
        "rev_original_usd_amount": originalUsdAmount,
        "rev_usd_conversion_rate": usdConversionRate,
        "rev_usd_conversion_date": usdConversionDate
    })
}

async function getCurrencyConversion(date, value) {
    await sendAPIRequest(
        "/conversionRate?base_currency=USD&currencies=EUR&date=" + date + "&value=" + value,
        "GET",
        {},
        "setCurrencyConversion"
    )
}

async function postADBJobRun() {
    await sendAPIRequest(
        "/adbjobrun",
        "POST",
        {}
    )
}

async function getRevenueLimits(sspId) {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");

    try {
        const response = await axios({
            method: "GET",
            url: baseUrl + "/revenueLimit/" + sspId,
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        return response.data;
    } catch (error) {
        console.error("Failed to fetch revenue limits:", error);
        throw error;
    }
}

async function putRevenueLimits(sspId, powerbiLimit, nautilusAbsLimit) {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");
    store.setIndicateLoading(true);

    try {
        await axios({
            method: "PUT",
            url: baseUrl + "/revenueLimit",
            data: {
                rvl_ssp_cntr: sspId,
                rvl_powerbi_corrected_limit: powerbiLimit,
                rvl_nautilus_absolute_limit: nautilusAbsLimit
            },
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        store.setIndicateLoading(false);
    } catch (error) {
        console.error("Failed to save revenue limits:", error);
        store.setIndicateError(true);
        throw error;
    }
}

async function getBillingNote(year, month, sspId) {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");

    try {
        console.log(`[API] Fetching billing note: year=${year}, month=${month}, sspId=${sspId}`);
        const response = await axios({
            method: "GET",
            url: baseUrl + "/billingNote/" + year + "/" + month + "/" + sspId,
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        console.log('[API] Billing note response:', response.data);
        console.log('[API] Response type:', typeof response.data);

        // If response.data is a string, parse it
        if (typeof response.data === 'string') {
            const parsed = JSON.parse(response.data);
            console.log('[API] Parsed billing note:', parsed);
            return parsed;
        }

        return response.data;
    } catch (error) {
        console.error("Failed to fetch billing note:", error);
        return null;
    }
}

async function putBillingNote(year, month, sspId, noteText) {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");
    store.setIndicateLoading(true);

    try {
        await axios({
            method: "PUT",
            url: baseUrl + "/billingNote",
            data: {
                bln_year: year,
                bln_month: month,
                bln_ssp_cntr: sspId,
                bln_note: noteText
            },
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        store.setIndicateLoading(false);
    } catch (error) {
        console.error("Failed to save billing note:", error);
        store.setIndicateError(true);
        throw error;
    }
}

async function getBillingNotesForYear(year) {
    let accessToken = localStorage.getItem("accessToken-storage");

    try {
        const response = await axios({
            method: "GET",
            url: baseUrl + "/billingNotes/" + year,
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        return response.data || [];
    } catch (error) {
        console.error("Failed to fetch billing notes for year:", error);
        return [];
    }
}

async function getRevenueMetadata() {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");

    try {
        const response = await axios({
            method: "GET",
            url: baseUrl + "/revenue/metadata",
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        return response.data;
    } catch (error) {
        console.error("Failed to fetch revenue metadata:", error);
        throw error;
    }
}

async function loadPowerBIRevenue() {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");
    store.setIndicateLoading(true);

    try {
        const response = await axios({
            method: "POST",
            url: baseUrl + "/revenue/load/powerbi",
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        store.setIndicateLoading(false);
        return response.data;
    } catch (error) {
        console.error("Failed to load PowerBI revenue:", error);
        store.setIndicateError(true);
        store.setIndicateLoading(false);
        throw error;
    }
}

async function loadNautilusRevenue() {
    const store = useMainStore();
    let accessToken = localStorage.getItem("accessToken-storage");
    store.setIndicateLoading(true);

    try {
        const response = await axios({
            method: "POST",
            url: baseUrl + "/revenue/load/nautilus",
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });
        store.setIndicateLoading(false);
        return response.data;
    } catch (error) {
        console.error("Failed to load Nautilus revenue:", error);
        store.setIndicateError(true);
        store.setIndicateLoading(false);
        throw error;
    }
}

async function refreshAll() {
    let accessToken = localStorage.getItem("accessToken-storage");
    const response = await axios({
        method: "POST",
        url: baseUrl + "/revenue/refresh-all",
        headers: {
            Authorization: 'Bearer ' + accessToken
        }
    });
    return response.data;
}

export default {
    baseUrl,
    monthNames,
    getSellingSidePlatform,
    patchSellingSidePlatform,
    getRevenue,
    getCampaignInfo,
    deleteRevenue,
    putRevenue,
    postADBJobRun,
    getCurrencyConversion,
    getRevenueLimits,
    putRevenueLimits,
    getBillingNote,
    putBillingNote,
    getBillingNotesForYear,
    getRevenueMetadata,
    loadPowerBIRevenue,
    loadNautilusRevenue,
    refreshAll
}